/**
 * The big-board camera's arithmetic, pure and DOM-free so it can be tested: when a
 * board is big, the reading size, the two zoom levels, where the camera goes to keep
 * the active answer in frame, which cells are on screen, and where the overlays sit.
 *
 * Coordinates: the camera is `translate(x, y) scale(s)` applied to the board (its frame
 * included), with the origin at the board's top-left. `cs` is the cell size at s = 1,
 * `fw` the board's frame width. A "window" is a range of whole cells.
 */

/** Grids up to 9×9 play whole; from 10×10 the board is read through the camera. */
export const BIG_BOARD_MIN = 10;

export function isBigBoard(model: { width: number; height: number }): boolean {
	return Math.max(model.width, model.height) >= BIG_BOARD_MIN;
}

/** Reading size: cells big enough for ≥ 11px clue text. 54px on narrow phones. */
export function readingCellSize(stageWidth: number): number {
	return stageWidth < 380 ? 54 : 56;
}

/** The margin the board may sit inside the stage when panned to an edge. */
export const EDGE = 12;

/**
 * Desktop and tablet: does the whole big board fit at reading size, with no camera?
 * `W` is the room the column may widen into (the viewport's width) and `H` the stage's
 * height (what the masthead and controls leave). The board needs its gutters either
 * side, the stage's bottom padding and an edge of air above. While playing the cell
 * never shrinks to make it fit, so clue text stays at its reading size. A finished board
 * (nothing left to type) may pass `minCell`: the board then shows whole at the largest
 * cell that fits, as long as that's still legible (≥ `minCell`). Returns the column width
 * the board needs and its cell size, or null when it doesn't fit (the camera stays).
 */
export function wholeBoardFit(
	room: { W: number; H: number },
	board: { cols: number; rows: number; frame: number; gutter: number; padBottom: number },
	minCell?: number,
): { column: number; cell: number } | null {
	const read = readingCellSize(room.W - 2 * board.gutter);
	const byWidth = (room.W - 2 * board.gutter - 2 * board.frame) / board.cols;
	const byHeight = (room.H - EDGE - board.padBottom - 2 * board.frame) / board.rows;
	const cell = minCell === undefined ? read : Math.min(read, Math.floor(Math.min(byWidth, byHeight)));
	if (cell < (minCell ?? read)) return null;
	const bw = board.cols * cell + 2 * board.frame;
	const bh = board.rows * cell + 2 * board.frame;
	const column = Math.ceil(bw + 2 * board.gutter);
	if (column > room.W || bh + EDGE + board.padBottom > room.H) return null;
	return { column, cell };
}

/** The smallest cell a finished big board shows whole at: clue text still ≥ 11px. */
export const FINISHED_MIN_CELL = 50;

/** Room for the board's shadow under its bottom edge. */
const SHADOW = 4;

export interface Cam {
	x: number;
	y: number;
	s: number;
}

export interface Stage {
	/** The stage (camera viewport), in px. */
	W: number;
	H: number;
	/** The board at s = 1 (frame included), in px. */
	bw: number;
	bh: number;
	/** Cell size and frame width at s = 1. */
	cs: number;
	fw: number;
	cols: number;
	rows: number;
}

/** The whole-board scale (never above reading size). */
export function fitScale(st: Stage): number {
	return Math.min(1, (st.W - 2 * EDGE) / st.bw, (st.H - 2 * EDGE) / st.bh);
}

/** The board can't leave the stage: centred on an axis it fits, else edge-bounded. */
export function clampCam(cam: Cam, st: Stage): Cam {
	const w = st.bw * cam.s;
	const h = st.bh * cam.s;
	const x = w <= st.W - 2 * EDGE ? (st.W - w) / 2 : Math.min(EDGE, Math.max(st.W - EDGE - w, cam.x));
	const y = h <= st.H - 2 * EDGE ? (st.H - h) / 2 : Math.min(EDGE, Math.max(st.H - EDGE - SHADOW - h, cam.y));
	return { x, y, s: cam.s };
}

/* ---- Zoom: two levels only ---------------------------------------------------------- */

export type ZoomLevel = 'read' | 'whole';

/** Below this scale the clue text gives way to arrows (it would be unreadable). */
export const FAR_SCALE = 0.8;

/** A pinch ends on whichever level is nearer. */
export function snapLevel(s: number, fit: number): ZoomLevel {
	return s < (fit + 1) / 2 ? 'whole' : 'read';
}

export function levelScale(level: ZoomLevel, fit: number): number {
	return level === 'whole' ? fit : 1;
}

/** Pinches can overshoot a little either way before they snap. */
export function pinchScale(s0: number, k: number, fit: number): number {
	return Math.max(fit * 0.9, Math.min(1.3, s0 * k));
}

/**
 * Zoom to scale `s` about the stage point (px, py). Zooming out keeps that point
 * under the finger; zooming in brings it to the centre (the tap said "there").
 */
export function zoomAbout(cam: Cam, s: number, px: number, py: number, st: Stage): Cam {
	const bx = (px - cam.x) / cam.s;
	const by = (py - cam.y) / cam.s;
	if (s >= 0.99) return clampCam({ s, x: st.W / 2 - bx * s, y: st.H / 2 - by * s }, st);
	return clampCam({ s, x: px - bx * s, y: py - by * s }, st);
}

/** Reading size, centred on a cell. */
export function centreOn(cell: number, st: Stage, rowShift = 0): Cam {
	const r = Math.floor(cell / st.cols);
	const c = cell % st.cols;
	return clampCam(
		{ s: 1, x: st.W / 2 - (c + 0.5) * st.cs - st.fw, y: st.H / 2 - (r - rowShift + 0.5) * st.cs - st.fw },
		st,
	);
}

/* ---- Following the solver ------------------------------------------------------------ */

export interface FollowTarget {
	/** The active answer's cells, in order. */
	cells: readonly number[];
	/** Its clue cell (kept in frame with it), or null. */
	clueCell: number | null;
	/** The cursor cell. */
	cursor: number;
	axis: 'across' | 'down';
}

/**
 * Which window of `cols × rows` whole cells keeps the answer and its clue in view,
 * starting from `origin` and moving as little as possible: only when something needed
 * leaves the window. An answer too long for the window keeps the cursor with one cell
 * behind and two ahead.
 */
export function followWindow(
	grid: { cols: number; rows: number },
	target: FollowTarget,
	origin: { x: number; y: number },
	cols: number,
	rows: number,
): { x: number; y: number } {
	const pts = [...target.cells, ...(target.clueCell === null ? [] : [target.clueCell])].map(
		(i) => [Math.floor(i / grid.cols), i % grid.cols] as const,
	);
	const cr = Math.floor(target.cursor / grid.cols);
	const cc = target.cursor % grid.cols;
	const axis = (lo: number, hi: number, pos: number, o: number, size: number, n: number) => {
		if (hi - lo + 1 > size) {
			lo = Math.max(lo, pos - 1);
			hi = Math.min(hi, pos + 2);
		}
		if (lo < o) o = lo;
		if (hi > o + size - 1) o = hi - size + 1;
		return Math.max(0, Math.min(n - size, o));
	};
	const rs = pts.map((p) => p[0]);
	const cs = pts.map((p) => p[1]);
	return {
		x: axis(Math.min(...cs), Math.max(...cs), cc, origin.x, cols, grid.cols),
		y: axis(Math.min(...rs), Math.max(...rs), cr, origin.y, rows, grid.rows),
	};
}

/** The camera may use the whole stage, edge to edge, when following. */
const FOLLOW_INSET = 2;

/**
 * Where the camera goes (at reading size) to keep `target` in frame, or null when it
 * is already in frame and nothing needs to move. Moves are whole cells, and a moved
 * window sits centred in the stage's fractional slack.
 */
export function followCam(cam: Cam, st: Stage, target: FollowTarget): Cam | null {
	if (cam.s < 0.99) return null;
	const E = FOLLOW_INSET;
	const vw = (st.W - 2 * E) / st.cs;
	const vh = (st.H - 2 * E - 6) / st.cs;
	const cols = Math.max(1, Math.floor(vw));
	const rows = Math.max(1, Math.floor(vh));
	const ox = (-cam.x + E - st.fw) / st.cs;
	const oy = (-cam.y + E - st.fw) / st.cs;
	const o = followWindow(st, target, { x: Math.ceil(ox - 0.01), y: Math.ceil(oy - 0.01) }, cols, rows);
	let nx = ox;
	let ny = oy;
	if (o.x < ox - 0.01 || o.x + cols > ox + vw + 0.01) nx = o.x - (vw - cols) / 2;
	if (o.y < oy - 0.01 || o.y + rows > oy + vh + 0.01) ny = o.y - (vh - rows) / 2;
	if (Math.abs(nx - ox) < 0.02 && Math.abs(ny - oy) < 0.02) return null;
	const next = clampCam({ s: 1, x: -(nx * st.cs) + E - st.fw, y: -(ny * st.cs) + E - st.fw }, st);
	if (Math.abs(next.x - cam.x) < 0.5 && Math.abs(next.y - cam.y) < 0.5) return null;
	return next;
}

/* ---- What's on screen ---------------------------------------------------------------- */

/** The frame in cell units (fractional): the board area the stage shows. */
export interface CellViewport {
	x: number;
	y: number;
	cols: number;
	rows: number;
}

export function cellViewport(cam: Cam, st: Stage): CellViewport {
	const unit = st.cs * cam.s;
	return {
		x: (-cam.x / cam.s - st.fw) / st.cs,
		y: (-cam.y / cam.s - st.fw) / st.cs,
		cols: st.W / unit,
		rows: st.H / unit,
	};
}

/** Cells wholly inside the frame (for Flow navigation's "currently visible"). */
export function visibleCells(cam: Cam, st: Stage): Set<number> {
	const v = cellViewport(cam, st);
	const eps = 0.02;
	const c0 = Math.max(0, Math.ceil(v.x - eps));
	const c1 = Math.min(st.cols - 1, Math.floor(v.x + v.cols + eps) - 1);
	const r0 = Math.max(0, Math.ceil(v.y - eps));
	const r1 = Math.min(st.rows - 1, Math.floor(v.y + v.rows + eps) - 1);
	const out = new Set<number>();
	for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) out.add(r * st.cols + c);
	return out;
}

/* ---- Overlays ------------------------------------------------------------------------ */

export type Corner = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

/** Minimap side in px: about a fifth of the stage, a whole number of px per cell. */
export function minimapSize(W: number, cols: number): number {
	return Math.max(cols * 4, Math.round(Math.min(78, W * 0.19) / cols) * cols);
}

/** Minimap margins from the stage edges (px). */
export const MINIMAP_INSET = { side: 10, top: 10, bottom: 14 } as const;

/**
 * The first corner (top-right, top-left, bottom-right, bottom-left) whose minimap
 * wouldn't cover the active answer. `box` is the answer's rect in stage px.
 */
export function minimapCorner(
	box: { x0: number; y0: number; x1: number; y1: number } | null,
	W: number,
	H: number,
	size: number,
): Corner {
	const corners: Corner[] = ['top-right', 'top-left', 'bottom-right', 'bottom-left'];
	if (!box) return corners[0]!;
	const P = size + 6; // the ink mount around the map
	for (const k of corners) {
		const L = k.endsWith('left') ? MINIMAP_INSET.side : W - MINIMAP_INSET.side - P;
		const T = k.startsWith('top') ? MINIMAP_INSET.top : H - MINIMAP_INSET.bottom - P;
		if (L + P < box.x0 || L > box.x1 || T + P < box.y0 || T > box.y1) return k;
	}
	return corners[0]!;
}

/** The answer's cells as a rect in stage px. */
export function cellsBox(cells: readonly number[], cam: Cam, st: Stage) {
	if (cells.length === 0) return null;
	let x0 = Infinity;
	let y0 = Infinity;
	let x1 = -Infinity;
	let y1 = -Infinity;
	for (const i of cells) {
		const r = Math.floor(i / st.cols);
		const c = i % st.cols;
		const x = cam.x + (st.fw + c * st.cs) * cam.s;
		const y = cam.y + (st.fw + r * st.cs) * cam.s;
		x0 = Math.min(x0, x);
		y0 = Math.min(y0, y);
		x1 = Math.max(x1, x + st.cs * cam.s);
		y1 = Math.max(y1, y + st.cs * cam.s);
	}
	return { x0, y0, x1, y1 };
}

/** Edge rulers: the visible fraction of the board on each axis, and where it starts. */
export function rulers(cam: Cam, st: Stage) {
	const w = st.bw * cam.s;
	const h = st.bh * cam.s;
	const fx = st.W / w;
	const fy = st.H / h;
	return {
		h: fx >= 1 ? null : { size: fx, start: Math.max(0, Math.min(1 - fx, -cam.x / w)) },
		v: fy >= 1 ? null : { size: fy, start: Math.max(0, Math.min(1 - fy, -cam.y / h)) },
	};
}

/**
 * Where the "FLOW ×n" tag sits relative to its cell, kept inside what the player can
 * see (`view`, in cells; the whole board when there's no camera) and off clue text:
 * above the cell unless that's the top visible row or would cover more clue cells than
 * the row below.
 */
export function flowTagPlacement(
	cell: number,
	grid: { cols: number; rows: number; isClue: (cell: number) => boolean },
	view: CellViewport = { x: 0, y: 0, cols: grid.cols, rows: grid.rows },
): { tx: number; above: boolean } {
	const r = Math.floor(cell / grid.cols);
	const c = cell % grid.cols;
	const first = { c: Math.max(0, Math.ceil(view.x - 0.02)), r: Math.max(0, Math.ceil(view.y - 0.02)) };
	const last = {
		c: Math.min(grid.cols - 1, Math.floor(view.x + view.cols + 0.02) - 1),
		r: Math.min(grid.rows - 1, Math.floor(view.y + view.rows + 0.02) - 1),
	};
	const tx = c >= last.c - 1 ? -88 : c <= first.c + 1 ? -12 : -50;
	const span = tx === -88 ? [c - 1, c] : tx === -12 ? [c, c + 1] : [c - 1, c, c + 1];
	const clues = (row: number) =>
		row < first.r || row > last.r
			? Infinity
			: span.filter((col) => col >= 0 && col < grid.cols && grid.isClue(row * grid.cols + col)).length;
	const above = r > first.r + 1 && clues(r - 1) <= clues(r + 1);
	return { tx, above };
}
